import { useCallback, useEffect, useState } from "react";
import { type Goal, type GoalView, type Today, api } from "./api.js";
import { ChatScreen } from "./screens/Chat.js";
import { GoalCardScreen } from "./screens/GoalCard.js";
import { GoalsScreen } from "./screens/Goals.js";
import { OracleScreen } from "./screens/Oracle.js";
import { SkillScreen } from "./screens/Skill.js";
import { TodayScreen } from "./screens/Today.js";

/**
 * Работают все шесть кадров: «Сегодня», «Цели», «Карточка цели», «Навык», «Чат»
 * и «Оракул» (раздел 10 CONCEPT.md — премортем, право на вероятность, разрешение
 * прогнозов и лента судьи).
 *
 * Визуальный язык — Liquid Glass (mockups/liquid-glass/). Фон приложения СВОЙ:
 * тему клиента Telegram мы игнорируем намеренно, потому что стеклу нужен фон
 * с глубиной, а двух хозяев у фона не бывает. Обоснование числами — в шапке tokens.css.
 */

type Tab = "today" | "goals" | "oracle" | "skill" | "chat";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        version?: string;
        isFullscreen?: boolean;
        ready?: () => void;
        expand?: () => void;
        /** Bot API 8.0. У клиентов постарше метода просто нет — отсюда проверка типа. */
        requestFullscreen?: () => void;
      };
    };
  }
}

export function App(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("today");
  const [openGoal, setOpenGoal] = useState<string | null>(null);
  const [today, setToday] = useState<Today | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [view, setView] = useState<GoalView | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    const [todayData, goalsData] = await Promise.all([
      api.get<Today>("/api/today"),
      api.get<{ goals: Goal[] }>("/api/goals")
    ]);
    setToday(todayData);
    setGoals(goalsData.goals);
    if (openGoal) setView(await api.get<GoalView>(`/api/goals/${openGoal}`));
  }, [openGoal]);

  useEffect(() => {
    void (async () => {
      const webApp = window.Telegram?.WebApp;
      webApp?.ready?.();
      /**
       * Полный экран — с 08.09 (решение владельца). Приложение занимает весь экран,
       * а не половину, и просит его само: режим запуска в BotFather работает только
       * для кнопки «Open App», а вход из меню бота или по прямой ссылке остался бы
       * обычным. `expand()` остаётся фолбэком для клиентов до Bot API 8.0 — там метода
       * нет вовсе, и звать его вслепую значит уронить ход инициализации.
       *
       * Геометрию берёт на себя CSS: в полноэкранном режиме сверху появляются кнопки
       * Telegram, и `--safe-top` складывает системный отступ с контентным (tokens.css).
       */
      if (typeof webApp?.requestFullscreen === "function") {
        try {
          webApp.requestFullscreen();
        } catch {
          webApp.expand?.();
        }
      } else {
        webApp?.expand?.();
      }
      try {
        if (webApp?.initData) {
          await api.post("/api/auth/telegram", { initData: webApp.initData });
        } else {
          // Вне Telegram работает только дев-вход, и только если он явно включён.
          await api.post("/api/auth/dev");
        }
        await reload();
      } catch (error) {
        setAuthError(error instanceof Error ? error.message : "не пускает");
      } finally {
        setReady(true);
      }
    })();
    // Первичная загрузка — один раз; дальше обновляемся по действиям.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!openGoal) {
      setView(null);
      return;
    }
    void api.get<GoalView>(`/api/goals/${openGoal}`).then(setView);
  }, [openGoal]);

  const onChanged = useCallback(() => void reload(), [reload]);

  if (!ready) {
    return (
      <>
        <Backdrop />
        <main className="phone" />
      </>
    );
  }

  if (authError) {
    return (
      <>
        <Backdrop />
        <main className="phone">
          <header className="head">
            <h1>goal-assistant</h1>
          </header>
          <p className="subhead">{authError}</p>
          <p className="empty">
            Вне Telegram нужен дев-вход: DEV_LOGIN_ENABLED=true в .env и петлевой HOST.
          </p>
        </main>
      </>
    );
  }

  return (
    <>
      <Backdrop />
      <main className="phone">
        {tab === "today" && today && !openGoal && (
          <TodayScreen today={today} onChanged={onChanged} onOpenGoal={(id) => { setOpenGoal(id); setTab("goals"); }} />
        )}

        {tab === "goals" && !openGoal && (
          <GoalsScreen goals={goals} onChanged={onChanged} onOpenGoal={setOpenGoal} />
        )}

        {openGoal && view && (
          <GoalCardScreen view={view} goals={goals} onChanged={onChanged} onBack={() => setOpenGoal(null)} />
        )}

        {tab === "oracle" && !openGoal && <OracleScreen />}
        {tab === "skill" && !openGoal && <SkillScreen />}
        {tab === "chat" && !openGoal && <ChatScreen />}
      </main>

      <nav className="tabbar">
        <div className="tabbar-inner glass-thick">
          {TABS.map(([key, label, icon]) => (
            <button
              key={key}
              className={`tab ${tab === key && !openGoal ? "on" : ""}`}
              aria-label={label}
              aria-current={tab === key && !openGoal ? "page" : undefined}
              onClick={() => {
                setOpenGoal(null);
                setTab(key);
              }}
            >
              <span className="ic">{icon}</span>
            </button>
          ))}
        </div>
      </nav>
    </>
  );
}

/**
 * Фон приложения: канвас плюс три мягких пятна. Без них стекло не читается —
 * на плоском тёмном все три плотности превращаются в один серый.
 *
 * Пятна закреплены к экрану (position: fixed в токенах), а не к ленте. Отсюда главное
 * следствие для контраста: любая панель на любой прокрутке может оказаться над центром
 * самого светлого пятна, и мерить надо именно это положение.
 */
function Backdrop(): React.JSX.Element {
  return (
    <div className="backdrop" aria-hidden="true">
      <i className="a" />
      <i className="b" />
      <i className="c" />
    </div>
  );
}

const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.9 } as const;

const TABS = [
  [
    "today",
    "Сегодня",
    <svg key="i" width="23" height="23" viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  ],
  [
    "goals",
    "Цели",
    <svg key="i" width="23" height="23" viewBox="0 0 24 24" {...stroke} strokeLinejoin="round">
      <path d="M12 3l8.5 9-8.5 9-8.5-9z" />
    </svg>
  ],
  [
    "oracle",
    "Оракул",
    <svg key="i" width="23" height="23" viewBox="0 0 24 24" {...stroke} strokeLinejoin="round">
      <path d="M12 3l2.2 6.1L20.5 11l-6.3 1.9L12 19l-2.2-6.1L3.5 11l6.3-1.9z" />
    </svg>
  ],
  [
    "skill",
    "Навык",
    <svg key="i" width="23" height="23" viewBox="0 0 24 24" {...stroke} strokeLinecap="round">
      <path d="M5 19V11" />
      <path d="M12 19V5" />
      <path d="M19 19v-5" />
    </svg>
  ],
  [
    "chat",
    "Чат",
    <svg key="i" width="23" height="23" viewBox="0 0 24 24" {...stroke} strokeLinejoin="round">
      <path d="M20 12a8 8 0 0 1-8 8H4l2.3-2.6A8 8 0 1 1 20 12z" />
    </svg>
  ]
] as const satisfies readonly (readonly [Tab, string, React.JSX.Element])[];
